import React, { useId, useState } from 'react';

import { Select, SelectOption } from '@flixbus/honeycomb-react';

import { CUSHION_CHOICES } from '../const';
import CushioningInputs from '../inputs/CushioningInputs';
import DeadheadInputs from '../inputs/DeadheadInputs';
import PostIncidentDrivingInputs from '../inputs/PostIncidentDrivingInputs';
import { CREATE_BREAKDOWN_FLOW_TEXT } from '../texts';

import styles from './BusDeadheadStep.module.css';

const CUSHION_TEXT = CREATE_BREAKDOWN_FLOW_TEXT.cushioning;

/**
 * Leaf step — Bus failure + Deadhead.
 *
 * The deadhead itself carries its driver on `deadhead.crewMember`, so there
 * is no separate "Deadhead driver" picker. The leaf keeps the full deadhead
 * object in local state and forwards it (`data.deadhead`) so the payload
 * mapper can read the driver id at submit time and the cushion-choice select
 * can render the driver's name inline.
 *
 * Emits `deadheadId`, `deadhead`, `driving`, `outboundTraveling`, and
 * `cushioningChoice` via `onDraftChange`. Shell owns the buttons and
 * validity.
 *
 * @param {import('common/activeElements/modals/FlowModal/flowTypes').FlowNodeComponentProps} props
 */
const BusDeadheadStep = ({ data, onDraftChange }) => {
	const cushionChoiceId = useId();

	const originalDriver = data?.assignment?.crewMember ?? null;

	const [deadheadId, setDeadheadId] = useState(data?.deadheadId ?? null);
	const [deadhead, setDeadhead] = useState(data?.deadhead ?? null);
	const [driving, setDriving] = useState({
		fromTimestamp: data?.driving?.fromTimestamp ?? '',
	});
	const [outboundTraveling, setOutboundTraveling] = useState({
		fromTimestamp: data?.outboundTraveling?.fromTimestamp ?? '',
		toTimestamp: data?.outboundTraveling?.toTimestamp ?? '',
	});
	const [cushioningChoice, setCushioningChoice] = useState(data?.cushioningChoice ?? '');

	const emitChange = (patch) => {
		onDraftChange?.({
			deadheadId,
			deadhead,
			driving,
			outboundTraveling,
			cushioningChoice,
			...patch,
		});
	};

	const handleDeadheadChange = (id, dh) => {
		setDeadheadId(id);
		setDeadhead(dh);
		emitChange({ deadheadId: id, deadhead: dh });
	};

	const handleDrivingChange = (nextDriving) => {
		setDriving(nextDriving);
		emitChange({ driving: nextDriving });
	};

	const handleOutboundChange = (nextOutbound) => {
		setOutboundTraveling(nextOutbound);
		emitChange({ outboundTraveling: nextOutbound });
	};

	const handleCushioningChoiceChange = (event) => {
		const next = event.target.value;
		setCushioningChoice(next);
		emitChange({ cushioningChoice: next });
	};

	const deadheadDriverName = deadhead?.crewMember?.fullName ?? '';
	const originalDriverName = originalDriver?.fullName ?? '';

	const cushionOptionLabel = (cushionChoiceValue) => {
		if (cushionChoiceValue === CUSHION_CHOICES.originalDriver) {
			return originalDriverName ? `${originalDriverName} (original driver)` : 'Original driver';
		}
		if (cushionChoiceValue === CUSHION_CHOICES.deadheadDriver) {
			return deadheadDriverName || 'Deadhead driver';
		}
		return cushionChoiceValue;
	};

	return (
		<div className={styles.stack}>
			<DeadheadInputs
				value={deadheadId}
				onChange={handleDeadheadChange}
				incidentTimestamp={data?.incidentTimestamp}
			/>
			<PostIncidentDrivingInputs
				value={driving}
				incidentTimestamp={data?.incidentTimestamp}
				onChange={handleDrivingChange}
			/>
			<Select
				id={cushionChoiceId}
				label={CUSHION_TEXT.driverLabel}
				value={cushioningChoice}
				onChange={handleCushioningChoiceChange}
				data-testid="cushion-choice-select"
			>
				<SelectOption value="" disabled>
					{CUSHION_TEXT.driverPlaceholder}
				</SelectOption>
				{Object.values(CUSHION_CHOICES).map((cushionChoiceValue) => (
					<SelectOption key={cushionChoiceValue} value={cushionChoiceValue}>
						{cushionOptionLabel(cushionChoiceValue)}
					</SelectOption>
				))}
			</Select>
			<CushioningInputs
				value={outboundTraveling}
				incidentTimestamp={data?.incidentTimestamp}
				onChange={handleOutboundChange}
			/>
		</div>
	);
};

export default BusDeadheadStep;
